import React from 'react'
import styled from 'styled-components'
import { useFormikContext } from 'formik'

import { Dropdown, TextInput } from 'components'
import AddonIcon from 'components/TextInput/AddonIcon'
import { DUP_CHECK_ERRORS } from 'modules/Leads/NewLead/constants'

// Utils and Services
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'
import useFormikError from 'hooks/use-formik-error'
import useBranch from 'hooks/use-branch'
import { useLeadFormConfigContext } from 'modules/Leads/context'
import { LeadDetails } from 'types/global'
import { EXAMPLE_URL_PLACEHOLDER } from 'utils/constants/url'

import DuplicateErrorMsg from 'modules/Leads/shared/DuplicateErrorMsg'

type FieldKey = keyof LeadDetails.LeadDetailsForm

const CompanyInformation: React.FC = () => {
  const { industries } = useWorkFlowLookup({})
  const { currency } = useBranch()
  const { values, getFieldProps, setFieldValue } = useFormikContext<LeadDetails.LeadDetailsForm>()
  const [getErrMsg] = useFormikError<LeadDetails.LeadDetailsForm>()
  const { requiredFieldNames } = useLeadFormConfigContext()

  const companyNameErr = getErrMsg('companyInfo.name' as FieldKey)
  const regNoErr = getErrMsg('companyInfo.registrationNumber' as FieldKey)
  const websiteErr = getErrMsg('companyInfo.website' as FieldKey)
  const isDuplicateCompanyName =
    companyNameErr === DUP_CHECK_ERRORS.EXACT_DUP_COMPANY_NAME ||
    companyNameErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_COMPANY_NAME
  const isRegNoDuplicate =
    regNoErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_REGISTRATION || regNoErr === DUP_CHECK_ERRORS.EXACT_DUP_REGISTRATION
  const isWebsiteDuplicate =
    websiteErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_URL || websiteErr === DUP_CHECK_ERRORS.EXACT_DUP_URL

  return (
    <Container>
      <CompanyInfoContainer>
        <InformationItem width={100}>
          <TextInput
            label="company_name_lbl"
            variant="labeled"
            required={requiredFieldNames.includes('company_info.name')}
            {...getFieldProps('companyInfo.name')}
            error={companyNameErr}
            errorVariant={companyNameErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_COMPANY_NAME ? 'warning' : 'error'}
            customError={
              isDuplicateCompanyName ? (
                <DuplicateErrorMsg
                  message={companyNameErr}
                  type={companyNameErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_COMPANY_NAME ? 'warning' : 'error'}
                />
              ) : undefined
            }
          />
        </InformationItem>
        <InformationItem width={100}>
          <Dropdown
            label="industry_one"
            name="companyInfo.industryId"
            onChange={(opt) => setFieldValue('companyInfo.industryId', opt.value)}
            options={industries}
            placeholder="please_select_lbl"
            value={
              values?.companyInfo?.industryId === ''
                ? null
                : industries.find((opt) => opt.value === values?.companyInfo?.industryId)
            }
            variant="labeled"
          />
        </InformationItem>
        <InformationItem width={100}>
          <TextInput
            label="reg_no_lbl"
            variant="labeled"
            placeholder="reg_no_placeholder"
            required={requiredFieldNames.includes('company_info.registration_number')}
            {...{
              ...getFieldProps('companyInfo.registrationNumber'),
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('companyInfo.registrationNumber', e.target.value.trim()),
            }}
            error={regNoErr}
            errorVariant={regNoErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_REGISTRATION ? 'warning' : 'error'}
            customError={
              isRegNoDuplicate ? (
                <DuplicateErrorMsg
                  message={regNoErr}
                  type={regNoErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_REGISTRATION ? 'warning' : 'error'}
                />
              ) : undefined
            }
          />
        </InformationItem>
        <InformationItem width={100}>
          <TextInput
            label="annual_revenue_lbl"
            variant="labeled"
            type="number"
            format="currency"
            placeholder="0.00"
            addonBefore={currency}
            {...getFieldProps('companyInfo.annualRevenue')}
            error={getErrMsg('companyInfo.annualRevenue' as FieldKey)}
          />
        </InformationItem>
        <InformationItem width={100}>
          <TextInput
            label="number_of_employees_lbl"
            variant="labeled"
            type="number"
            format="whole-positive"
            placeholder="0"
            {...getFieldProps('companyInfo.numEmployees')}
            error={getErrMsg('companyInfo.numEmployees' as FieldKey)}
          />
        </InformationItem>
        <InformationItem width={100}>
          <TextInput
            label="website_lbl"
            variant="labeled"
            type="url"
            placeholder={EXAMPLE_URL_PLACEHOLDER}
            addonBefore={<AddonIcon position="before" icon={['fa', 'link']} />}
            {...getFieldProps('companyInfo.website')}
            error={websiteErr}
            errorVariant={websiteErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_URL ? 'warning' : 'error'}
            customError={
              isWebsiteDuplicate ? (
                <DuplicateErrorMsg
                  message={websiteErr}
                  type={websiteErr === DUP_CHECK_ERRORS.POTENTIAL_DUP_URL ? 'warning' : 'error'}
                />
              ) : undefined
            }
          />
        </InformationItem>
      </CompanyInfoContainer>
    </Container>
  )
}

const Container = styled.div`
  flex: 1;
`

const CompanyInfoContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  padding: 16px 20px 16px 44px;
`

const InformationItem = styled.div<{ width?: number }>`
  width: ${(p) => (p.width ? `${p.width}%` : '100%')};
  align-items: flex-start;
  padding: 5px 0;
`

export default CompanyInformation
